import {
    FOCUS_RING_NO_RADIUS,
    FOCUS_WITHIN_RING,
    getPasswordManagerAttributes,
    Icon,
    useAnchoredDropdownStyle,
    useModalPortalTarget,
} from '@amelio/ui-library';
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import type { EmployeeSearchList } from '../hooks/useEmployeeSearchList';

const SEARCH_RESULTS_ID = 'objective-employee-search-results';

interface ObjectiveEmployeeSearchFieldProps {
    search: EmployeeSearchList;
    /** Names the field, its chevron and its result list. */
    label: string;
    placeholder: string;
    hasError?: boolean;
    /** The chips of what is already picked, drawn before the search text. */
    selection: ReactNode;
    /** The results, shown while the list is open. */
    children: ReactNode;
}

/**
 * The dropdown trigger: a neutral-fill field holding the picked chips before the search text, with
 * a chevron at the end, and its result list.
 */
export default function ObjectiveEmployeeSearchField({
    search,
    label,
    placeholder,
    hasError,
    selection,
    children,
}: ObjectiveEmployeeSearchFieldProps) {
    const { searchText, isListOpen, type, dismiss, toggle } = search;

    // The results are rendered in the app's portal, anchored to the field, exactly as the library's
    // own Select and MultiSelect do: the objective form's cards carry a backdrop blur, which opens
    // a stacking context, and a list left in the flow is trapped inside its card and drawn over by
    // the sections below it, whatever its z-index (the period fields' calendar has the same cause).
    const fieldRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const portalTarget = useModalPortalTarget();
    const listStyle = useAnchoredDropdownStyle({ anchorRef: fieldRef, isOpen: isListOpen });

    // A press anywhere else closes the list, as Escape does. The list is portaled out of the
    // field, so both are checked: the shared click-outside hooks watch a single element.
    useEffect(() => {
        if (!isListOpen) return;

        const dismissOnOutsidePress = (event: MouseEvent) => {
            const path = event.composedPath();
            const isInside = [fieldRef.current, listRef.current].some(
                (element) => element && path.includes(element),
            );
            if (!isInside) dismiss();
        };

        document.addEventListener('mousedown', dismissOnOutsidePress);
        return () => document.removeEventListener('mousedown', dismissOnOutsidePress);
    }, [isListOpen, dismiss]);

    return (
        <div
            ref={fieldRef}
            data-testid={'objective-employee-search'}
        >
            <div
                className={`bg-surface-field flex min-h-[46px] w-full flex-wrap items-center gap-1.5 rounded-lg border px-3 py-2 ${FOCUS_WITHIN_RING} ${
                    hasError ? 'border-red-500' : 'border-transparent'
                }`}
            >
                {selection}

                <input
                    {...getPasswordManagerAttributes()}
                    type={'text'}
                    role={'combobox'}
                    aria-expanded={isListOpen}
                    aria-controls={SEARCH_RESULTS_ID}
                    aria-autocomplete={'list'}
                    aria-label={label}
                    value={searchText}
                    onChange={(event) => type(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                            dismiss();
                        }

                        // The picker lives inside the objective form: Enter here means
                        // "search", never "save the objective".
                        if (event.key === 'Enter') {
                            event.preventDefault();
                        }
                    }}
                    placeholder={placeholder}
                    className={
                        'placeholder:text-obj-slate min-w-[120px] flex-1 bg-transparent text-[14px] outline-none'
                    }
                    data-testid={'objective-employee-search-input'}
                />

                <button
                    type={'button'}
                    onClick={toggle}
                    aria-label={label}
                    aria-expanded={isListOpen}
                    aria-controls={SEARCH_RESULTS_ID}
                    className={`rounded-md p-1 ${FOCUS_RING_NO_RADIUS}`}
                    data-testid={'objective-employee-toggle-list'}
                >
                    <Icon
                        name={'arrow-arrow-down'}
                        size={'lg'}
                        colorClassName={'text-obj-muted'}
                    />
                </button>
            </div>

            {isListOpen &&
                createPortal(
                    <div
                        ref={listRef}
                        id={SEARCH_RESULTS_ID}
                        role={'listbox'}
                        aria-label={label}
                        style={listStyle}
                        className={
                            'scrollbar-glass max-h-[320px] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg'
                        }
                        data-testid={'objective-employee-search-results'}
                    >
                        {children}
                    </div>,
                    portalTarget || document.body,
                )}
        </div>
    );
}
